import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Coffee, Heart, Briefcase, Clock, Award, TrendingUp } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyLeaveBalance = ({ user }) => {
  const [balance, setBalance] = useState(null);
  const [leaveHistory, setLeaveHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users/${user.id}/leave-balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch leave balance:', error);
    }
  }, [user.id]);

  const fetchLeaveHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/leave-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter to only show current user's leaves
      const myLeaves = response.data.filter(l => l.user_id === user.id && l.status === 'approved');
      setLeaveHistory(myLeaves);
    } catch (error) {
      console.error('Failed to fetch leave history:', error);
    }
  }, [user.id]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchBalance(), fetchLeaveHistory()]);
      setLoading(false);
    };
    loadData();
  }, [fetchBalance, fetchLeaveHistory]);

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const calculateDays = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    return diffDays;
  };

  const leaveTypes = [
    { key: 'casual', label: 'Casual Leave', icon: Coffee, color: '#3B82F6', remaining: balance?.casual_remaining || 0, used: balance?.casual_used || 0 },
    { key: 'sick', label: 'Sick Leave', icon: Heart, color: '#EF4444', remaining: balance?.sick_remaining || 0, used: balance?.sick_used || 0 },
    { key: 'annual', label: 'Annual Leave', icon: Calendar, color: '#10B981', remaining: balance?.annual_remaining || 0, used: balance?.annual_used || 0 },
    { key: 'personal', label: 'Personal Leave', icon: Briefcase, color: '#F59E0B', remaining: balance?.personal_remaining || 0, used: balance?.personal_used || 0 },
  ];

  if (loading) {
    return (
      <div className="content-area" style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>Loading your leave balance...</p>
      </div>
    );
  }

  const totalRemaining = leaveTypes.reduce((sum, lt) => sum + lt.remaining, 0);
  const totalUsed = leaveTypes.reduce((sum, lt) => sum + lt.used, 0);

  return (
    <div className="content-area" data-testid="my-leave-balance-page">
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Award size={32} color="#7077FE" />
          My Leave Balance
        </h1>
        <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)' }}>Year {new Date().getFullYear()}</p>
      </div>

      {/* Summary Card */}
      <div style={{
        padding: '24px',
        background: 'linear-gradient(135deg, #7077FE20, #10B98120)',
        borderRadius: '16px',
        border: '1px solid var(--border-color)',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: '24px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Total Available</div>
            <div style={{ fontSize: '40px', fontWeight: '700', color: '#10B981' }}>{totalRemaining}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>days remaining</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Used This Year</div>
            <div style={{ fontSize: '40px', fontWeight: '700', color: '#F59E0B' }}>{totalUsed}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>days taken</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Unpaid Taken</div>
            <div style={{ fontSize: '40px', fontWeight: '700', color: '#6B7280' }}>{balance?.unpaid_taken || 0}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>days</div>
          </div>
        </div>
      </div>

      {/* Leave Type Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        {leaveTypes.map(leaveType => {
          const Icon = leaveType.icon;
          const total = leaveType.remaining + leaveType.used;
          const percentage = total > 0 ? (leaveType.remaining / total) * 100 : 0;
          
          return (
            <div
              key={leaveType.key}
              className="card"
              style={{ padding: '20px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: `${leaveType.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon size={22} color={leaveType.color} />
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{leaveType.label}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>{total} days allocated</div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{
                  height: '8px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '4px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${percentage}%`,
                    background: leaveType.color,
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: leaveType.color }}>{leaveType.remaining}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>remaining</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-secondary)' }}>{leaveType.used}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>used</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Leave History */}
      <div className="card" style={{ padding: '24px' }}>
        <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Clock size={18} />
          Approved Leave History
        </h3>

        {leaveHistory.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
            <Calendar size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p>No approved leaves yet this year</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {leaveHistory.map(leave => {
              const leaveTypeInfo = leaveTypes.find(lt => lt.key === leave.leave_type) || { color: '#6B7280', label: leave.leave_type };
              return (
                <div
                  key={leave.id}
                  style={{
                    padding: '16px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '10px',
                    borderLeft: `4px solid ${leaveTypeInfo.color}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '600',
                        background: `${leaveTypeInfo.color}20`,
                        color: leaveTypeInfo.color,
                        marginBottom: '8px',
                        textTransform: 'capitalize'
                      }}>
                        {leave.leave_type.replace('_', ' ')} Leave
                      </span>
                      <div style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                        {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                        {calculateDays(leave.start_date, leave.end_date)} day{calculateDays(leave.start_date, leave.end_date) > 1 ? 's' : ''}
                      </div>
                    </div>
                    {leave.approved_by_name && (
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>Approved by</div>
                        <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{leave.approved_by_name}</div>
                      </div>
                    )}
                  </div>
                  {leave.reason && (
                    <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {leave.reason}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyLeaveBalance;
